import { PageSize } from '@/constant';
import {
	GetForeignApplicantListParams,
	GetWorkerApplicantListsDetailProgramParams,
	IForeignApplicant,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { formatForeignApplicantList, RootState } from '..';
import {
	IForeignApplicantDetailStore,
	IForeignApplicantListStore,
	IForeignApplicantProgramListStore,
	IPayloadCreateForeignApplicantRequest,
	IPayloadDeleteForeignApplicantRequest,
	IPayloadGetForeignApplicantDetailRequest,
	IPayloadGetForeignApplicantDetailSuccess,
	IPayloadGetForeignApplicantListRequest,
	IPayloadGetForeignApplicantListSuccess,
	IPayloadGetForeignApplicantProgramListRequest,
	IPayloadGetForeignApplicantProgramListSuccess,
	IPayloadResetPasswordRequest,
} from './foreign-applicant.type';

const InitParams: GetForeignApplicantListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const InitListsDetailProgramParams: GetWorkerApplicantListsDetailProgramParams =
	{
		page: 1,
		pageSize: PageSize.S,
		id: 0,
	};

const initialState = {
	fetching: false,
	foreignApplicantList: [] as IForeignApplicantListStore[],
	total: 0,
	totalProgram: 0,
	params: InitParams,
	foreignApplicantDetail: {} as IForeignApplicantDetailStore,
	foreignApplicantProgramList: [] as IForeignApplicantProgramListStore[],
	paramsListsDetailProgram: InitListsDetailProgramParams,
};

const foreignApplicantSlice = createSlice({
	initialState,
	name: 'foreignApplicant',
	reducers: {
		/**---- GET FOREIGN_APPLICANT LIST START ---- */
		getForeignApplicantListRequest: (
			state,
			action: PayloadAction<IPayloadGetForeignApplicantListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getForeignApplicantListSuccess: (
			state,
			action: PayloadAction<IPayloadGetForeignApplicantListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.foreignApplicantList = content;
			state.total = total;
		},
		getForeignApplicantListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.foreignApplicantList = [];
		},
		/**---- GET FOREIGN_APPLICANT LIST END ---- */

		/**---- CREATE FOREIGN_APPLICANT START ---- */
		createForeignApplicantRequest: (
			state,
			action: PayloadAction<IPayloadCreateForeignApplicantRequest>
		) => {
			state.fetching = true;
		},
		createForeignApplicantSuccess: (state) => {
			state.fetching = false;
		},
		createForeignApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE FOREIGN_APPLICANT END ---- */

		/**---- DELETE FOREIGN_APPLICANT START ---- */
		deleteForeignApplicantRequest: (
			state,
			action: PayloadAction<IPayloadDeleteForeignApplicantRequest>
		) => {
			state.fetching = true;
		},
		deleteForeignApplicantSuccess: (state) => {
			state.fetching = false;
		},
		deleteForeignApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE FOREIGN_APPLICANT END ---- */

		/**---- GET FOREIGN_APPLICANT DETAIL START ---- */
		getForeignApplicantDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetForeignApplicantDetailRequest>
		) => {
			state.fetching = true;
		},
		getForeignApplicantDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetForeignApplicantDetailSuccess>
		) => {
			const { foreignApplicantDetail } = action.payload;
			state.fetching = false;
			state.foreignApplicantDetail = foreignApplicantDetail;
		},
		getForeignApplicantDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET FOREIGN_APPLICANT DETAIL END ---- */

		/**---- GET APPLICANT PROGRAM LIST START ---- */
		getForeignApplicantProgramListRequest: (
			state,
			action: PayloadAction<IPayloadGetForeignApplicantProgramListRequest>
		) => {
			state.fetching = true;
			state.paramsListsDetailProgram = action?.payload;
		},
		getForeignApplicantProgramListSuccess: (
			state,
			action: PayloadAction<IPayloadGetForeignApplicantProgramListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.foreignApplicantProgramList = content;
			state.totalProgram = total;
		},
		getForeignApplicantProgramListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET APPLICANT PROGRAM LIST END ---- */

		updateForeignApplicantSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IForeignApplicant>>
		) => {
			const { data } = action.payload;
			const foreignApplicantList = [...state.foreignApplicantList];
			state.foreignApplicantList = formatForeignApplicantList(
				mergeListItemUpdated<IForeignApplicantListStore, IForeignApplicant>(
					foreignApplicantList,
					data
				)
			);
		},
		updateForeignApplicantDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IForeignApplicant>>
		) => {
			const { data } = action.payload;
			const foreignApplicantDetail = { ...state.foreignApplicantDetail };
			state.foreignApplicantDetail = mergeItemUpdated(
				foreignApplicantDetail,
				data
			);
		},
		resetForeignApplicantDetail: (state) => {
			state.foreignApplicantDetail = {} as IForeignApplicantDetailStore;
		},
		/**---- RESET PASSWORD START ---- */
		resetPasswordRequest: (
			state,
			action: PayloadAction<IPayloadResetPasswordRequest>
		) => {
			state.fetching = true;
		},
		resetPasswordSuccess: (state) => {
			state.fetching = false;
		},
		resetPasswordFailure: (state) => {
			state.fetching = false;
		},
		/**---- RESET PASSWORD END ---- */
	},
});

export const ForeignApplicantSelectors = {
	selectFetching: (state: RootState) => state.foreignApplicant.fetching,
	selectParams: (state: RootState) => state.foreignApplicant.params,
	selectList: (state: RootState) => state.foreignApplicant.foreignApplicantList,
	selectTotal: (state: RootState) => state.foreignApplicant.total,
	selectDetail: (state: RootState) =>
		state.foreignApplicant.foreignApplicantDetail,
	selectTotalProgram: (state: RootState) => state.foreignApplicant.totalProgram,
	selectProgramList: (state: RootState) =>
		state.foreignApplicant.foreignApplicantProgramList,
	selectParamsListsDetailProgram: (state: RootState) =>
		state.foreignApplicant.paramsListsDetailProgram,
};

export const ForeignApplicantActions = foreignApplicantSlice.actions;
export const ForeignApplicantReducer = foreignApplicantSlice.reducer;
